import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./profile.css";

// Add this import for animation
import { CSSTransition } from "react-transition-group";
import DoughnutChart from "../../test/DoughnutChart";
import { Chart } from "chart.js";
import Loader from "../../../../utils/loader/loader";
import ToastMessage from "../../../../utils/toasts/toastMessage";
import {
  editProfile,
  profileCompletionPercentage,
  profileDetails,
} from "../../../../Redux/profile/profile.action";
import { useNavigate } from "react-router-dom";
import {
  BOARD_OPTIONS,
  DIVISION_OPTIONS,
  SUBJECT_OPTIONS,
} from "../../../../utils/subjectBoard";
import { countryList } from "../../../../utils/countryCode";

const ConcentricCircleChart = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const dataCC = {
      labels: ["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8"],
      datasets: [
        {
          data: [80, 100],
          backgroundColor: ["#73A4F4", "#F3F4F6"],
          borderWidth: 0,
        },
        {
          data: [150, 100],
          backgroundColor: ["transparent", "transparent"],
          borderWidth: 0,
          weight: 0.75,
        },
        {
          data: [150, 100],
          backgroundColor: ["#21CBAD", "#F3F4F6"],
          borderWidth: 0,
        },
        {
          data: [150, 100],
          backgroundColor: ["transparent", "transparent"],
          borderWidth: 0,
          weight: 0.75,
        },
        {
          data: [130, 100],
          backgroundColor: ["#91D1EA", "#F3F4F6"],
          borderWidth: 0,
        },
        {
          data: [150, 100],
          backgroundColor: ["transparent", "transparent"],
          borderWidth: 0,
          borderRadius: 0,
          weight: 0.75,
        },
        {
          data: [60, 100],
          backgroundColor: ["#F9969A", "#F3F4F6"],
          borderWidth: 0,
        },
        {
          data: [150, 100],
          backgroundColor: ["transparent", "transparent"],
          borderWidth: 0,
          weight: 0.75,
        },
      ],
    };

    const chartInstance = new Chart(ctx, {
      type: "doughnut",
      data: dataCC,
      options: {
        responsive: true,
        layout: {
          padding: {
            top: -10,
            left: 10,
            bottom: 0,
            right: 35,
          },
        },
        plugins: {
          tooltip: {
            enabled: true,
          },
          legend: {
            display: false,
          },
          datalabels: {
            display: false,
          },
        },
      },
      plugins: [
        {
          id: "outerLabels",
          afterDraw: (chart) => {
            const {
              ctx,
              chartArea: { width, height, left, top },
              data,
            } = chart;
            const labels = data.labels;
            ctx.save();
            ctx.font = "11px Inter";
            ctx.fillStyle = "#A79E9E";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const centerX = (width + left) / 2;
            const centerY = (height + top) / 2;
            const radius = (Math.min(width, height) / 2) * 0.938;

            labels.forEach((label, index) => {
              const angle = (index / labels.length) * 2 * Math.PI - Math.PI / 2;
              const x = centerX + (radius + 30) * Math.cos(angle);
              const y = centerY + (radius + 30) * Math.sin(angle);
              ctx.fillText(label, x, y);
            });
            ctx.restore();
          },
        },
      ],
    });

    return () => {
      chartInstance.destroy();
    };
  }, []);

  return <canvas ref={canvasRef} width={400} height={400} />;
};

// ─── helpers for the new row-based class selector ───────────────────────────

const makeRow = () => ({
  id: Date.now() + Math.random(),
  grade: "",
  section: "",
  openSection: false,
  openSubject: false,
});

const defaultDivisions = Array.from({ length: 26 }, (_, i) =>
  String.fromCharCode(65 + i),
);

/** Convert classRows → payload array expected by the API */
const buildPayloadFromRows = (rows) =>
  rows
    .filter((r) => r.grade)
    .map((r) => ({
      grade: String(r.grade),
      division: r.section || "",
      subject: r.subject,
    }));

/** Convert API classes array → classRows — empty array if no data */
const hydrateRows = (classes = []) => {
  if (!classes || !classes?.length) return [];
  return classes?.map((item) => ({
    ...makeRow(),
    grade: item?.grade || "",
    section: item?.division || "",
    subject: item?.subject || [],
  }));
};

const ClassRow = ({
  row,
  isEditable,
  onChange,
  onRemove,
  onAdd,
  closeOthers,
}) => {
  const rowRef = useRef(null);
  const sectionRef = useRef(null);
  const subjectRef = useRef(null);

  // Close SECTION dropdown only when clicking outside the section box
  useEffect(() => {
    if (!row.openSection) return;
    const handler = (e) => {
      if (sectionRef.current && !sectionRef.current.contains(e.target)) {
        onChange(row.id, { openSection: false });
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [row.id, row.openSection, onChange]);

  // Close SUBJECT dropdown only when clicking outside the subject box
  useEffect(() => {
    if (!row.openSubject) return;
    const handler = (e) => {
      if (subjectRef.current && !subjectRef.current.contains(e.target)) {
        onChange(row.id, { openSubject: false });
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [row.id, row.openSubject, onChange]);

  const toggleSubject = (subj) => {
    if (subj == null || subj === "") return;
    const subject = row?.subject || [];
    const updated = subject.includes(subj)
      ? subject.filter((s) => s !== subj)
      : [...subject, subj];
    onChange(row.id, { subject: updated });
  };

  const sectionLabel = row.section || "Section";
  const subjectLabel =
    row.subject?.filter(Boolean)?.length > 0
      ? row?.subject?.filter(Boolean)?.join(", ")
      : "subject";

  const openSection = () => {
    closeOthers(row.id);
    onChange(row.id, { openSection: !row.openSection, openSubject: false });
  };

  const openSubject = () => {
    closeOthers(row.id);
    onChange(row.id, { openSubject: !row.openSubject, openSection: false });
  };

  return (
    <div
      ref={rowRef}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
      }}
    >
      {/* ── CLASS: 1–10 native select ── */}
      <div style={{ flex: "0 0 72px" }}>
        <select
          className="fz18 form-control"
          disabled={!isEditable}
          value={row.grade}
          onChange={(e) => onChange(row.id, { grade: e.target.value })}
          style={{ height: "38px", padding: "6px 8px", width: "100%" }}
        >
          <option value="">Class</option>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {/* ── SECTION: single select (radio) ── */}
      <div style={{ position: "relative", flex: 1 }}>
        <div
          className="fz18 form-control"
          style={{
            cursor: isEditable ? "pointer" : "default",
            userSelect: "none",
            height: "38px",
            padding: "6px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
          onClick={() => isEditable && openSection()}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "calc(100% - 18px)",
            }}
          >
            {sectionLabel}
          </span>
          {isEditable && (
            <span style={{ flexShrink: 0 }}>{row.openSection ? "▲" : "▼"}</span>
          )}
        </div>
        {row.openSection && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: "6px",
              zIndex: 50,
              padding: "8px",
              maxHeight: "200px",
              overflowY: "auto",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: "6px",
              }}
            >
              {defaultDivisions.map((div) => (
                <label
                  key={div}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name={`section-${row.id}`}
                    checked={row.section === div}
                    onChange={() =>
                      onChange(row.id, { section: div, openSection: false })
                    }
                  />
                  {div}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── SUBJECT: multi-select (checkbox) ── */}
      <div ref={subjectRef} style={{ position: "relative", flex: 1 }}>
        <div
          className="fz18 form-control"
          style={{
            cursor: isEditable ? "pointer" : "default",
            userSelect: "none",
            height: "38px",
            padding: "6px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
          onClick={() => isEditable && openSubject()}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "calc(100% - 18px)",
            }}
          >
            {subjectLabel}
          </span>
          {isEditable && (
            <span style={{ flexShrink: 0 }}>{row.openSubject ? "▲" : "▼"}</span>
          )}
        </div>
        {row?.openSubject && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: "6px",
              zIndex: 50,
              padding: "6px",
              maxHeight: "200px",
              overflowY: "auto",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {SUBJECT_OPTIONS.map((subj) => (
              <label
                key={subj}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  cursor: "pointer",
                  padding: "4px 0",
                }}
              >
                <input
                  type="checkbox"
                  checked={row?.subject?.includes(subj)}
                  onChange={() => toggleSubject(subj)}
                />
                {subj}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* ── + add  /  − remove  (inline) ── */}
      {isEditable && (
        <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
          <button
            type="button"
            onClick={onAdd}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              border: "1px solid #aaa",
              background: "#fff",
              cursor: "pointer",
              fontSize: "18px",
              lineHeight: 1,
              color: "#555",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
            }}
            title="Add row"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => onRemove(row.id)}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              border: "1px solid #ccc",
              background: "#fff",
              cursor: "pointer",
              fontSize: "18px",
              lineHeight: 1,
              color: "#bbb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
            }}
            title="Remove row"
          >
            −
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main ProfileBody ─────────────────────────────────────────────────────────

const ProfileBody = ({ onProfileUpdateSuccess }) => {
  const dispatch = useDispatch();
  const nav = useNavigate();
  const store = useSelector((state) => state);
  const [loading, setLoading] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });

  const [showCloseBtn, setShowCloseBtn] = useState(false);

  const [openCountryDropdown, setOpenCountryDropdown] = useState(false);

  const teacherDetails = useSelector(
    (state) => state?.ProfileManager?.user_details,
  );
  const profile_completion = useSelector(
    (state) => state?.ProfileManager?.completion?.profile_completion,
  );

  const [isEditable, setIsEditable] = useState(false);
  const [fields, setFields] = useState({
    class: teacherDetails?.grade || "",
    school: teacherDetails?.school || "",
    board: teacherDetails?.board || "",
    city: teacherDetails?.address || "",
    subject: teacherDetails?.subject || "",
    division: teacherDetails?.divisions || "",
    country: teacherDetails?.country || "India",
    employee_id: teacherDetails?.employee_id || "",
    phone: teacherDetails?.phone_number || "",
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(0);
  const tabsRef = useRef(null);
  const selectorRef = useRef(null);

  const tabs = ["History Table 1", "History Table 2", "History Table 3"];

  const [missingFields, setMissingFields] = useState({});

  // ── row-based class state — starts empty, no pre-filled row ──────────────
  const [classRows, setClassRows] = useState([]);

  useEffect(() => {
    if (profile_completion < 40) {
      setIsEditable(true);
    } else {
      setIsEditable(false);
    }
  }, [profile_completion]);

  useEffect(() => {
    if (!profile_completion) {
      setLoading(true);
    } else setLoading(false);
  }, [profile_completion]);

  const updateRow = (id, patch) =>
    setClassRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    );

  const addRow = () => setClassRows((prev) => [...prev, makeRow()]);

  const removeRow = (id) =>
    setClassRows((prev) => prev.filter((r) => r.id !== id));

  // Close all OTHER rows' dropdowns — called when opening a dropdown in a row
  const closeOthers = (exceptId) =>
    setClassRows((prev) =>
      prev.map((r) =>
        r.id !== exceptId
          ? { ...r, openSection: false, openSubject: false }
          : r,
      ),
    );

  // sync classRows → fields.class (keeps validation happy)
  useEffect(() => {
    const value = buildPayloadFromRows(classRows)
      .map((c) => (c.division ? `${c.grade}-${c.division}` : c.grade))
      .join(", ");
    setFields((prev) => ({ ...prev, class: value }));
  }, [classRows]);

  const [openBoardDropdown, setOpenBoardDropdown] = useState(false);

  const streams = ["Arts", "Science", "Commerce"];

  //  outside click close the dropdown content
  useEffect(() => {
    const handleClickOutside = (e) => {
      const dropdowns = [
        { className: "board-dropdown", setter: setOpenBoardDropdown },
        { className: "country-dropdown", setter: setOpenCountryDropdown },
      ];

      dropdowns.forEach(({ className, setter }) => {
        if (!e.target.closest(`.${className}`)) {
          setter(false);
        }
      });
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    dispatch(profileDetails());
    dispatch(profileCompletionPercentage());
  }, [dispatch]);

  useEffect(() => {
    if (!teacherDetails) return;

    setFields({
      class: teacherDetails?.classes || "",
      school: teacherDetails?.school || "",
      board: teacherDetails?.board || "",
      city: teacherDetails?.address || "",
      subject: teacherDetails?.subject || "",
      country: teacherDetails?.country || "",
      employee_id: teacherDetails?.employee_id || "",
      phone: teacherDetails?.phone_number || "",
    });

    // Hydrate classRows from API data
    const hydrated = hydrateRows(teacherDetails?.classes || []);
    setClassRows(hydrated);
  }, [teacherDetails]);

  // Load user details into fields when switching to edit mode
  // const handleEdit = () => {
  //   setShowCloseBtn(true);
  //   setFields({
  //     class: teacherDetails?.grade || "",
  //     division: teacherDetails?.divisions || "",
  //     school: teacherDetails?.school || "",
  //     board: teacherDetails?.board || "",
  //     city: teacherDetails?.address || "",
  //     subject: teacherDetails?.subject || "",
  //     country: teacherDetails?.country || "",
  //     employee_id: teacherDetails?.employee_id || "",
  //     phone: teacherDetails?.phone_number || "",
  //   });
  //   setIsEditable(true);
  // };

  const originalDataRef = useRef(null);

  const snapshotCurrentData = () => {
    const classPayload = buildPayloadFromRows(classRows);
    // sort so row order doesn't cause false "changed" detection
    const normalizedClasses = [...classPayload]
      .map((c) => ({ ...c, subject: [...(c.subject || [])].sort() }))
      .sort((a, b) =>
        (a.grade + a.division).localeCompare(b.grade + b.division),
      );

    return JSON.stringify({
      school: fields.school,
      board: fields.board,
      city: fields.city,
      country: fields.country,
      employee_id: fields.employee_id,
      phone: fields.phone,
      classes: normalizedClasses,
    });
  };

  const handleEdit = () => {
    setShowCloseBtn(true);
    const initialFields = {
      class: teacherDetails?.grade || "",
      division: teacherDetails?.divisions || "",
      school: teacherDetails?.school || "",
      board: teacherDetails?.board || "",
      city: teacherDetails?.address || "",
      subject: teacherDetails?.subject || "",
      country: teacherDetails?.country || "",
      employee_id: teacherDetails?.employee_id || "",
      phone: teacherDetails?.phone_number || "",
    };
    setFields(initialFields);
    setIsEditable(true);

    const normalizedClasses = hydrateRows(teacherDetails?.classes || [])
      .map((r) => ({
        grade: String(r.grade || ""),
        division: r.section || "",
        subject: [...(r.subject || [])].sort(),
      }))
      .sort((a, b) =>
        (a.grade + a.division).localeCompare(b.grade + b.division),
      );

    originalDataRef.current = JSON.stringify({
      school: initialFields.school,
      board: initialFields.board,
      city: initialFields.city,
      country: initialFields.country,
      employee_id: initialFields.employee_id,
      phone: initialFields.phone,
      classes: normalizedClasses,
    });
  };

  const handleCloseBtn = () => {
    setShowCloseBtn(false);
    setIsEditable(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value;

    setFields((prev) => ({ ...prev, [name]: nextValue }));
    if (missingFields[name])
      setMissingFields((prev) => ({ ...prev, [name]: false }));
  };

  const moveSelector = () => {
    const tabEls = tabsRef.current?.querySelectorAll("a");
    const targetTab = tabEls?.[nextIndex];
    const selector = selectorRef.current;

    if (targetTab && selector) {
      selector.style.left = `${targetTab.offsetLeft}px`;
      selector.style.width = `${targetTab.offsetWidth}px`;
      selector.style.top = `${targetTab.offsetTop}px`;
      selector.style.height = `${targetTab.offsetHeight}px`;
    }
  };

  useEffect(() => {
    moveSelector();

    const timer = setTimeout(() => {
      setActiveIndex(nextIndex);
    }, 300); // delay matches CSS transition duration

    window.addEventListener("resize", moveSelector);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", moveSelector);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextIndex]);

  const handleApply = async (e) => {
    e.preventDefault();
    setLoading(true);

    const requiredFields = [
      "school",
      "board",
      "city",
      "country",
      "employee_id",
      "phone",
    ];

    const emptyFields = requiredFields.reduce((acc, field) => {
      if (!String(fields[field] || "").trim()) acc[field] = true;
      return acc;
    }, {});

    // Also validate class selection
    const classPayload = buildPayloadFromRows(classRows);
    if (!classPayload.length) {
      emptyFields.class = true;
    }

    if (Object.keys(emptyFields).length > 0) {
      const missingFieldNames = Object.keys(emptyFields)
        .map((field) => field.replace("_", " "))
        .join(", ");

      setToastState({
        show: true,
        message: `Please fill the mandatory field: ${missingFieldNames}`,
        type: "error",
      });

      setMissingFields(emptyFields);
      setLoading(false);
      return;
    }
    if (
      fields.employee_id &&
      !/^[A-Za-z0-9-]{3,20}$/.test(fields.employee_id.trim())
    ) {
      setToastState({
        show: true,
        message:
          "Employee ID must be 3-20 characters (letters, numbers, hyphens only).",
        type: "error",
      });
      setMissingFields((prev) => ({ ...prev, employee_id: true }));
      setLoading(false);
      return;
    }

    if (fields.phone && !/^[6-9]\d{9}$/.test(fields.phone.trim())) {
      setToastState({
        show: true,
        message: "Please enter a valid 10-digit Indian phone number.",
        type: "error",
      });
      setMissingFields((prev) => ({ ...prev, phone: true }));
      setLoading(false);
      return;
    }

    setMissingFields({});

    const currentSnapshot = (() => {
      const normalizedClasses = classPayload
        .map((c) => ({ ...c, subject: [...(c.subject || [])].sort() }))
        .sort((a, b) =>
          (a.grade + a.division).localeCompare(b.grade + b.division),
        );
      return JSON.stringify({
        school: fields.school,
        board: fields.board,
        city: fields.city,
        country: fields.country,
        employee_id: fields.employee_id,
        phone: fields.phone,
        classes: normalizedClasses,
      });
    })();

    if (
      originalDataRef.current &&
      currentSnapshot === originalDataRef.current
    ) {
      setToastState({
        show: true,
        message: "No changes detected. Nothing to update.",
        type: "error",
      });
      setShowCloseBtn(false);
      setIsEditable(false);
      setLoading(false);
      return;
    }

    const payload = {
      classes: classPayload,
      school: fields.school,
      board: fields.board,
      address: fields.city,
      country: fields.country,
      employee_id: fields.employee_id,
      phone_number: fields.phone,
    };

    const result = await dispatch(editProfile(payload));

    if (result.success) {
      setToastState({
        show: true,
        message: "Profile updated successfully.",
        type: "success",
      });

      onProfileUpdateSuccess();
      dispatch(profileCompletionPercentage());
      setShowCloseBtn(false);
      setIsEditable(false);
      originalDataRef.current = currentSnapshot;
    } else {
      setToastState({
        show: true,
        message: result.message || "Something went wrong! Try again.",
        type: "error",
      });
    }

    setLoading(false);
    // setIsEditable(false);
    // setShowCloseBtn(false);
  };

  return (
    <div className="profile">
      <div className="profile__info">
        <div className="info-left">
          <div className="bdrs40 blue-block">
            <div className="w-100 title-bar">
              <div className="title-bar__left">
                <img
                  src="./assets/images/inner-pages/icons/icon-nav-user.svg"
                  alt=""
                />
                <div className="name">
                  <h2 className="text-capitalize mb-1 fw700 fz36">
                    {teacherDetails?.first_name} {teacherDetails?.last_name}
                  </h2>
                  {/* <p className="m-0">Your Recent Works</p> */}
                </div>
              </div>
              {showCloseBtn ? (
                <button
                  className="btn btn-solid btn-solid--dark"
                  onClick={handleCloseBtn}
                >
                  <span className="fw500 fz16 text-nowrap">Close</span>
                </button>
              ) : (
                <button
                  className="btn btn-solid btn-solid--dark"
                  onClick={handleEdit}
                >
                  <span className="fw500 fz16 text-nowrap">
                    {isEditable ? "Complete Your Profile" : "Edit profile"}
                  </span>
                </button>
              )}
            </div>
            <hr className="sep" />
            <h4 className="lh1 fz24 fw700">Personal info</h4>

            {/* ── ROW 1: School | Board | City ── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "12px",
              }}
            >
              {/* School/Institute */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <label className="fz14 fw500" style={{ color: "inherit" }}>
                  School/Institute{" "}
                  <span className={isEditable ? "error-mandate" : ""}>*</span>
                </label>
                <input
                  type="text"
                  className={`fz14 form-control ${missingFields.school ? "is-invalid" : ""}`}
                  name="school"
                  readOnly={!isEditable}
                  value={fields?.school}
                  maxLength={50}
                  onChange={handleInputChange}
                  style={{ padding: "6px 10px", height: "36px" }}
                />
              </div>

              {/* Select Board */}
              <div
                className="board-dropdown"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  position: "relative",
                }}
              >
                <label className="fz14 fw500">
                  Select Board{" "}
                  <span className={isEditable ? "error-mandate" : ""}>*</span>
                </label>
                <div
                  className={`fz14 form-control ${missingFields.board ? "is-invalid" : ""}`}
                  style={{
                    cursor: isEditable ? "pointer" : "default",
                    userSelect: "none",
                    padding: "6px 10px",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isEditable) setOpenBoardDropdown((p) => !p);
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fields.board ? String(fields.board) : "Select Board"}
                  </span>
                  {isEditable && (
                    <span style={{ flexShrink: 0 }}>
                      {openBoardDropdown ? "▲" : "▼"}
                    </span>
                  )}
                </div>
                {openBoardDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                      zIndex: 20,
                      padding: "6px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {BOARD_OPTIONS.map((item) => (
                      <label
                        key={item}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "4px 0",
                          cursor: "pointer",
                          fontSize: "13px",
                        }}
                      >
                        <input
                          type="radio"
                          name="board"
                          checked={fields.board === item}
                          disabled={!isEditable}
                          onChange={() => {
                            handleInputChange({
                              target: { name: "board", value: item },
                            });
                            setOpenBoardDropdown(false);
                          }}
                        />{" "}
                        {item}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* City */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <label className="fz14 fw500">
                  City{" "}
                  <span className={isEditable ? "error-mandate" : ""}>*</span>
                </label>
                <input
                  type="text"
                  className={`fz14 form-control ${missingFields.city ? "is-invalid" : ""}`}
                  name="city"
                  readOnly={!isEditable}
                  value={fields?.city}
                  maxLength={50}
                  onChange={handleInputChange}
                  style={{ padding: "6px 10px", height: "36px" }}
                />
              </div>
            </div>

            {/* ── ROW 2: Emp ID | Country | Phone | Email ── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "12px",
                marginBottom: "20px",
              }}
            >
              {/* Employee ID */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <label className="fz14 fw500">
                  Employee ID{" "}
                  <span className={isEditable ? "error-mandate" : ""}>*</span>
                </label>
                <input
                  type="text"
                  className={`fz14 form-control ${missingFields.employee_id ? "is-invalid" : ""}`}
                  name="employee_id"
                  readOnly={!isEditable}
                  value={fields?.employee_id}
                  onChange={handleInputChange}
                  style={{ padding: "6px 10px", height: "36px" }}
                />
              </div>

              {/* Country */}
              {/* Country */}
              <div
                className="country-dropdown"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  position: "relative",
                }}
              >
                <label className="fz14 fw500">
                  Country{" "}
                  <span className={isEditable ? "error-mandate" : ""}>*</span>
                </label>
                <div
                  className={`fz14 form-control ${missingFields.country ? "is-invalid" : ""}`}
                  style={{
                    cursor: isEditable ? "pointer" : "default",
                    userSelect: "none",
                    padding: "6px 10px",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isEditable) setOpenCountryDropdown((p) => !p);
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fields.country ? String(fields.country) : "Select Country"}
                  </span>
                  {isEditable && (
                    <span style={{ flexShrink: 0 }}>
                      {openCountryDropdown ? "▲" : "▼"}
                    </span>
                  )}
                </div>
                {openCountryDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                      zIndex: 20,
                      padding: "6px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {countryList.map((item) => (
                      <label
                        key={item.code}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "4px 0",
                          cursor: "pointer",
                          fontSize: "13px",
                        }}
                      >
                        <input
                          type="radio"
                          name="country"
                          checked={fields.country === item.name}
                          disabled={!isEditable}
                          onChange={() => {
                            handleInputChange({
                              target: { name: "country", value: item.name },
                            });
                            setOpenCountryDropdown(false);
                          }}
                        />{" "}
                        {item.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Phone */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <label className="fz14 fw500">
                  Phone Number{" "}
                  <span className={isEditable ? "error-mandate" : ""}>*</span>
                </label>
                <input
                  type="text"
                  className={`fz14 form-control ${missingFields.phone ? "is-invalid" : ""}`}
                  name="phone"
                  readOnly={!isEditable}
                  value={fields?.phone}
                  onChange={handleInputChange}
                  maxLength={10}
                  inputMode="numeric"
                  style={{ padding: "6px 10px", height: "36px" }}
                />
              </div>

              {/* Email */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <label className="fz14 fw500">Email</label>
                <input
                  type="email"
                  required
                  disabled
                  className="fz14 form-control"
                  name="email"
                  value={teacherDetails?.email}
                  style={{ padding: "6px 10px", height: "36px" }}
                />
              </div>
            </div>

            {/* ── CLASS / GRADES — badge row UI (below all fields) ── */}
            <div style={{ marginBottom: "8px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <span className="fz18 fw500">
                  Class / Grades{" "}
                  <span className={isEditable ? "error-mandate" : ""}>*</span>
                </span>
                {missingFields.class && (
                  <span style={{ fontSize: "12px", color: "#dc3545" }}>
                    Please select at least one class
                  </span>
                )}
              </div>

              {/* Column headers — only when rows exist */}
              {classRows.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginBottom: "4px",
                    paddingLeft: "2px",
                  }}
                >
                  <span
                    style={{
                      flex: "0 0 72px",
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#888",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Class
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#888",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Section
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#888",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Subject
                  </span>
                  {isEditable && <span style={{ width: "64px" }} />}
                </div>
              )}

              {/* Rows */}
              {classRows.map((row) => (
                <ClassRow
                  key={row.id}
                  row={row}
                  isEditable={isEditable}
                  onChange={updateRow}
                  onRemove={removeRow}
                  onAdd={addRow}
                  closeOthers={closeOthers}
                />
              ))}

              {/* Empty state: "Add Classes" button */}
              {classRows.length === 0 && isEditable && (
                <button
                  type="button"
                  onClick={addRow}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "none",
                    border: "1px dashed #aaa",
                    borderRadius: "8px",
                    padding: "7px 16px",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#777",
                  }}
                >
                  <span
                    style={{
                      fontSize: "20px",
                      lineHeight: 1,
                      fontWeight: 300,
                      color: "#555",
                    }}
                  >
                    +
                  </span>
                  Add Classes
                </button>
              )}

              {/* Read-only empty */}
              {classRows.length === 0 && !isEditable && (
                <span style={{ fontSize: "14px", color: "#aaa" }}>—</span>
              )}
            </div>
            <CSSTransition
              in={isEditable}
              timeout={300}
              classNames="fade"
              unmountOnExit
            >
              <>
                <span className="fz18">
                  <span className="error-mandate">* </span> mandatory fields
                </span>
                <button
                  className="btn btn-solid btn-solid--dark btn-apply"
                  type="submit"
                  onClick={(e) => handleApply(e)}
                >
                  Apply
                </button>
              </>
            </CSSTransition>
          </div>

          {/* Rest of the component remains unchanged */}
          {/* <div className="graph-block">
            <h3 className="fz28 fw900">Overview</h3>
            <div className="chart-block hide-below-768">
              <div className="grad-block grad-block--dark">
                <div className="grad-block__chart">
                  <DoughnutChart
                    elementId="yellowDonut"
                    color="#fff"
                    dataValue={70}
                    graphBorder="64%"
                  />
                </div>
                <div className="text-white grad-block__text">
                  <div className="fw900 fz22">175</div>
                  <p className="m-0 fw500 lh1 fz16">Test Performance</p>
                </div>
              </div>
              <div className="grad-block grad-block--dark">
                <div className="grad-block__chart">
                  <DoughnutChart
                    elementId="cyanDonut"
                    color="#fff"
                    dataValue={60}
                    graphBorder="64%"
                  />
                </div>
                <div className="text-white grad-block__text">
                  <div className="fw900 fz22">20</div>
                  <p className="m-0 fw500 lh1 fz16">Code Practice</p>
                </div>
              </div>
              <div className="grad-block grad-block--dark">
                <div className="grad-block__chart">
                  <DoughnutChart
                    elementId="limegreenDonut"
                    color="#fff"
                    dataValue={30}
                    graphBorder="64%"
                  />
                </div>
                <div className="text-white grad-block__text">
                  <div className="fw900 fz22">152</div>
                  <p className="m-0 fw500 lh1 fz16">Total Performance</p>
                </div>
              </div>
            </div>
            <div className="m-carousel chart-block hide-above-768">
              <div className="owl-carousel owl-carousel--user owl-theme">
                <div className="item">
                  <div className="grad-block grad-block--purple">
                    <div className="grad-block__chart">
                      <canvas id="m-yellowDonut" />
                    </div>
                    <div className="text-white grad-block__text">
                      <div className="fw900 fz22">175</div>
                      <p className="m-0 fw500 lh1 fz16">Test Performance</p>
                    </div>
                  </div>
                </div>
                <div className="item">
                  <div className="grad-block grad-block--blue">
                    <div className="grad-block__chart">
                      <canvas id="m-cyanDonut" />
                    </div>
                    <div className="text-white grad-block__text">
                      <div className="fw900 fz22">20</div>
                      <p className="m-0 fw500 lh1 fz16">Code Practice</p>
                    </div>
                  </div>
                </div>
                <div className="item">
                  <div className="grad-block grad-block--orange">
                    <div className="grad-block__chart">
                      <canvas id="m-limegreenDonut" />
                    </div>
                    <div className="text-white grad-block__text">
                      <div className="fw900 fz22">152</div>
                      <p className="m-0 fw500 lh1 fz16">Total Performance</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> */}
        </div>
        <div className="info-right" style={{ zIndex: 1 }}>
          <div className="bdrs40 checklist-content">
            <div className="text-center">
              <h3 className="fw700 fz28 text-white  show-above-768 hide-below-768">
                Complete your profile
              </h3>
            </div>
            <div className="success-chart">
              <DoughnutChart
                elementId="profileSuccessDonut"
                color="#32FB6F"
                dataValue={profile_completion || 0}
                graphBorder="78%"
              />
              <div className="fw700 text-white fz44 inner-num">
                {profile_completion || 0}%
              </div>
            </div>
            <ul className="list-inline checklist">
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-01"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={!!(teacherDetails?.classes?.length > 0)}
                    readOnly
                  />
                  <label htmlFor="cklt-01">
                    <b />
                    <span className="ps-1">Class/ Grades:</span>
                  </label>
                </div>
              </li>

              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-01"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={!!teacherDetails?.classes?.length}
                    readOnly
                  />
                  <label htmlFor="cklt-01">
                    <b />
                    <span className="ps-1">Divisions:</span>
                  </label>
                </div>
              </li>

              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-02"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(
                        teacherDetails?.school &&
                        teacherDetails?.school !== "NA"
                      )
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-02">
                    <b />
                    <span className="ps-1">School/Institute</span>
                  </label>
                </div>
              </li>
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-03"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(
                        teacherDetails?.address &&
                        teacherDetails?.address !== "NA"
                      )
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-03">
                    <b />
                    <span className="ps-1">City (State)</span>
                  </label>
                </div>
              </li>
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-04"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(
                        teacherDetails?.classes?.length &&
                        teacherDetails.classes.some(
                          (c) =>
                            Array.isArray(c?.subject) &&
                            c.subject.filter(Boolean).length > 0,
                        )
                      )
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-04">
                    <b />
                    <span className="ps-1">Subject</span>
                  </label>
                </div>
              </li>
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-05"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(
                        teacherDetails?.board && teacherDetails?.board !== "NA"
                      )
                    }
                    readOnly
                  />
                  <label htmlFor="cklt-05">
                    <b />
                    <span className="ps-1">Board</span>
                  </label>
                </div>
              </li>
              <li>
                <div className="ps-1 fw500 fz20 checkbox-container">
                  <input
                    id="cklt-06"
                    type="checkbox"
                    name="cklt"
                    className="cklt-input cklt-toggle"
                    checked={
                      !!(
                        teacherDetails?.employee_id &&
                        teacherDetails?.employee_id !== "NA"
                      )
                    }
                  />
                  <label htmlFor="cklt-06">
                    <b />
                    <span className="ps-1">Employee_ID</span>
                  </label>
                </div>
              </li>
            </ul>
            <div className="cover-btn">
              <h3 className="fw700 fz24 text-white show-below-768 hide-above-768">
                Complete your profile
              </h3>
              <button
                type="button"
                className="btn fw500 fz18 btn-solid btn-solid--white"
              >
                {profile_completion === 100 ? "Completed" : "Not completed"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* <div className="profile__analyze">
        <div className="analyze-title">
          <h2 className="fw900 fz28">Analyze</h2>
          <p className="fz18 fw500">
          
          </p>
        </div>
        <div className="analyze-main">
          <div className="bdrs40 analyze-main__left">
            <div className="bdrs40 bg-inner">
              <div className="bg-inner__left">
                <div className="chart-text">
                  <span className="fz10 text-uppercase">Average level</span>
                  <div className="fw700 fz18 num">A4</div>
                </div>
                <ConcentricCircleChart />
              </div>
              <div className="bg-inner__right">
                <ul className="list-inline h-100">
                  <li>
                    <div className="col-pair">
                      <div className="color color--red" />
                      <div className="fz16 text">React</div>
                    </div>
                  </li>
                  <li>
                    <div className="col-pair">
                      <div className="color color--blue" />
                      <div className="fz16 text">Java</div>
                    </div>
                  </li>
                  <li>
                    <div className="col-pair">
                      <div className="color color--green" />
                      <div className="fz16 text">Python</div>
                    </div>
                  </li>
                  <li>
                    <div className="col-pair">
                      <div className="color color--darkBlue" />
                      <div className="fz16 text">PHP</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="bdrs40 fut-assignments analyze-main__right">
            <h2 className="text-white fw700 fz28 title">Future Assignment</h2>
            <div className="accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="fz18 fw500 text-white accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseOne"
                    aria-expanded="true"
                    aria-controls="collapseOne"
                  >
                    Hindi
                  </button>
                </h2>
                <div
                  id="collapseOne"
                  className="accordion-collapse collapse show"
                  data-bs-parent="#accordionExample"
                >
                  <div className="pt-0 accordion-body">
                    <ul className="list-inline mb-0 pb-2 f-assign-list">
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-02.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-02.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

             
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="fz18 fw500 text-white accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseTwo"
                    aria-expanded="false"
                    aria-controls="collapseTwo"
                  >
                    Computer
                  </button>
                </h2>
                <div
                  id="collapseTwo"
                  className="accordion-collapse collapse"
                  data-bs-parent="#accordionExample"
                >
                  <div className="pt-0 accordion-body">
                    <ul className="list-inline mb-0 pb-2 f-assign-list">
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-02.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="fz18 fw500 text-white accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseThree"
                    aria-expanded="false"
                    aria-controls="collapseThree"
                  >
                    English
                  </button>
                </h2>
                <div
                  id="collapseThree"
                  className="accordion-collapse collapse"
                  data-bs-parent="#accordionExample"
                >
                  <div className="pt-0 accordion-body">
                    <ul className="list-inline mb-0 pb-2 f-assign-list">
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-02.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="f-assign">
                          <div className="f-assign__title">
                            <div className="img-cover">
                              <img
                                src="./assets/images/inner-pages/icons/icon-fut-01.svg"
                                alt=""
                              />
                            </div>
                            <h3 className="fw700 fz20 text-white mb-0">
                              For some integer m, every even integer is of the
                              for voluptates minus ipsum corrupti voluptatem
                              itaque, temporibus quis perspiciatis cum?
                            </h3>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* <div className="bdrs28 dashboard__history profile__history">
        <div className="history-title">
          <h2 className="fw700 mb-0 fz28">History Table</h2>
          <a href="#" className="fz18 fw500 see-all">
            See All
          </a>
        </div>

        <nav
          className="tabs relative d-flex gap-2 position-relative"
          ref={tabsRef}
        >
          <div className="selector absolute ts03" ref={selectorRef}></div>
          {tabs.map((tab, index) => (
            <a
              key={tab}
              href="#"
              className={`fz16 fw500   ${
                activeIndex === index ? "active-tab" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                setNextIndex(index);
              }}
            >
              {tab}
            </a>
          ))}
        </nav>

        <section className="tab-body">
          {tabs.map((tab, index) => (
            <div
              key={tab}
              style={{ display: activeIndex === index ? "block" : "none" }}
            >
              <ul className="w-100 list-unstyled h-table-list">
                <li>
                  <button className="w-100 border-0 ts03 h-data select">
                    <div className="rounded-circle img-cover img-cover--purple">
                      <img
                        src="./assets/images/inner-pages/icons/icon-math.svg"
                        alt=""
                      />
                    </div>
                    <div className="text">
                      <h4 className="fz16 fw500">
                        At vero eos et accusamus et iusto odio dignissimos
                        ducimus qui blanditiis praesentium voluptatum deleniti
                        atque corrupti quos dolores et quas molestias excepturi
                        sint occaecati cupiditate non provident, similique sunt
                        in culpa qui officia deserunt mollitia animi, id est
                        laborum et dolorum fuga.
                      </h4>
                      <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                    </div>
                  </button>
                </li>
                <li>
                  <button className="w-100 border-0 ts03 h-data">
                    <div className="rounded-circle img-cover img-cover--yellow">
                      <img
                        src="./assets/images/inner-pages/icons/icon-science.svg"
                        alt=""
                      />
                    </div>
                    <div className="text">
                      <h4 className="fz16 fw500">
                        At vero eos et accusamus et iusto odio dignissimos
                        ducimus qui blanditiis praesentium voluptatum deleniti
                        atque corrupti quos dolores et quas molestias excepturi
                        sint occaecati cupiditate non provident, similique sunt
                        in culpa qui officia deserunt mollitia animi, id est
                        laborum et dolorum fuga.
                      </h4>
                      <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                    </div>
                  </button>
                </li>
                <li>
                  <button className="w-100 border-0 ts03 h-data">
                    <div className="rounded-circle img-cover img-cover--red">
                      <img
                        src="./assets/images/inner-pages/icons/icon-english.svg"
                        alt=""
                      />
                    </div>
                    <div className="text">
                      <h4 className="fz16 fw500">
                        At vero eos et accusamus et iusto odio dignissimos
                        ducimus qui blanditiis praesentium voluptatum deleniti
                        atque corrupti quos dolores et quas molestias excepturi
                        sint occaecati cupiditate non provident, similique sunt
                        in culpa qui officia deserunt mollitia animi, id est
                        laborum et dolorum fuga.
                      </h4>
                      <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                    </div>
                  </button>
                </li>
                <li>
                  <button className="w-100 border-0 ts03 h-data">
                    <div className="rounded-circle img-cover img-cover--yellow">
                      <img
                        src="./assets/images/inner-pages/icons/icon-science.svg"
                        alt=""
                      />
                    </div>
                    <div className="text">
                      <h4 className="fz16 fw500">
                        At vero eos et accusamus et iusto odio dignissimos
                        ducimus qui blanditiis praesentium voluptatum deleniti
                        atque corrupti quos dolores et quas molestias excepturi
                        sint occaecati cupiditate non provident, similique sunt
                        in culpa qui officia deserunt mollitia animi, id est
                        laborum et dolorum fuga.
                      </h4>
                      <div className="fz10 fw500 time">02:20 AM, 03-Nov</div>
                    </div>
                  </button>
                </li>
              </ul>
            </div>
          ))}
        </section>
        <Loader show={loading} />
        <ToastMessage
          show={toastState.show}
          onClose={() => setToastState({ show: false, message: "", type: "" })}
          message={toastState.message}
          type={toastState.type}
        />
      </div> */}
      <Loader show={loading} />
      <ToastMessage
        show={toastState.show}
        onClose={() => setToastState({ show: false, message: "", type: "" })}
        message={toastState.message}
        type={toastState.type}
      />
    </div>
  );
};

export default ProfileBody;
